/*!
 * Copyright © 2020-2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Action, createReducer, on } from '@ngrx/store';

import {
  EApiRequestStatus,
  IFailureProps,
  IRequestStatusProps,
  ISendVerificationCodeRequestProps,
  ISendVerificationCodeSuccessProps
} from '../../models';
import {
  ResendVerificationCodeFailure,
  ResendVerificationCodeRequest,
  ResendVerificationCodeSuccess,
  ResetAccountActivation,
  SendVerificationCodeFailure,
  SendVerificationCodeRequest,
  SendVerificationCodeSuccess,
  SetCredentialsFailure,
  SetCredentialsRequest,
  SetCredentialsSuccess,
  UpdateRequestStatus
} from '../actions';
import { initialUserState, IUserState } from '../state';

const reducer = createReducer(
  initialUserState,
  on(
    SendVerificationCodeRequest,
    (state: IUserState, { username, password }: ISendVerificationCodeRequestProps): IUserState => ({
      ...state,
      setPasswordRequestStatus: EApiRequestStatus.Request,
      resendCodeRequestStatus: EApiRequestStatus.Initial,
      verifyAccountRequestStatus: EApiRequestStatus.Initial,
      username,
      password
    })
  ),
  on(
    SendVerificationCodeSuccess,
    (state: IUserState, { sessionToken }: ISendVerificationCodeSuccessProps): IUserState => ({
      ...state,
      setPasswordRequestStatus: EApiRequestStatus.Success,
      verificationAttempts: initialUserState.verificationAttempts,
      resendAttempts: initialUserState.resendAttempts,
      sessionToken
    })
  ),
  on(
    SendVerificationCodeFailure,
    (state: IUserState, { error }: IFailureProps): IUserState => ({
      ...state,
      setPasswordRequestStatus: EApiRequestStatus.Failure
    })
  ),
  on(
    ResendVerificationCodeRequest,
    (state: IUserState): IUserState => ({
      ...state,
      resendCodeRequestStatus: EApiRequestStatus.Request
    })
  ),
  on(
    ResendVerificationCodeSuccess,
    (state: IUserState, { sessionToken }: ISendVerificationCodeSuccessProps): IUserState => ({
      ...state,
      resendCodeRequestStatus: EApiRequestStatus.Success,
      resendAttempts: state.resendAttempts + 1,
      sessionToken
    })
  ),
  on(
    ResendVerificationCodeFailure,
    (state: IUserState, { error }: IFailureProps): IUserState => ({
      ...state,
      resendCodeRequestStatus: EApiRequestStatus.Failure
    })
  ),
  on(
    SetCredentialsRequest,
    (state: IUserState): IUserState => ({
      ...state,
      verifyAccountRequestStatus: EApiRequestStatus.Request
    })
  ),
  on(
    SetCredentialsSuccess,
    (state: IUserState): IUserState => ({
      ...state,
      verifyAccountRequestStatus: EApiRequestStatus.Success
    })
  ),
  on(
    SetCredentialsFailure,
    (state: IUserState, { error }: IFailureProps): IUserState => ({
      ...state,
      verifyAccountRequestStatus: EApiRequestStatus.Failure,
      verificationAttempts: state.verificationAttempts + 1
    })
  ),
  on(
    UpdateRequestStatus,
    (state: IUserState, props: IRequestStatusProps): IUserState => ({
      ...state,
      setPasswordRequestStatus: props.setPasswordRequestStatus || state.setPasswordRequestStatus,
      resendCodeRequestStatus: props.resendCodeRequestStatus || state.resendCodeRequestStatus,
      verifyAccountRequestStatus: props.verifyAccountRequestStatus || state.verifyAccountRequestStatus
    })
  ),
  on(
    ResetAccountActivation,
    (state: IUserState): IUserState => ({
      ...initialUserState,
      username: state.username
    })
  )
);

export function userReducer(state: IUserState | undefined, action: Action): IUserState {
  return reducer(state, action);
}
